import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Trash2, LogOut, Bell, CalendarDays, Hourglass, BadgeCheck } from "lucide-react";
import { API } from "@/App";
import AdminGallery from "@/components/AdminGallery";
import AdminNotifications from "@/components/AdminNotifications";

const formatDateFR = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const todayISO = () => new Date().toISOString().split("T")[0];

export default function AdminDashboard({ token, onLogout }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayOnly, setTodayOnly] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reservations`, { headers });
      setReservations(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expirée, veuillez vous reconnecter.");
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [headers, onLogout]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const confirm = async (r) => {
    try {
      const { data } = await axios.patch(`${API}/admin/reservations/${r.id}/confirm`, {}, { headers });
      setReservations((list) => list.map((x) => (x.id === r.id ? { ...x, ...data.reservation } : x)));
      if (data.email_sent) {
        toast.success(`Réservation de ${r.name} confirmée`, {
          description: `Email de confirmation envoyé à ${data.reservation.email}`,
          icon: <Bell className="h-4 w-4" />,
        });
      } else {
        toast.warning(`Réservation de ${r.name} confirmée`, {
          description: "L'email de confirmation n'a pas pu être envoyé (adresse manquante ou erreur d'envoi).",
        });
      }
    } catch {
      toast.error("Impossible de confirmer cette réservation.");
    }
  };

  const remove = async (r) => {
    try {
      await axios.delete(`${API}/admin/reservations/${r.id}`, { headers });
      setReservations((list) => list.filter((x) => x.id !== r.id));
      toast.success(`Réservation de ${r.name} supprimée.`);
    } catch {
      toast.error("Impossible de supprimer cette réservation.");
    }
  };

  const visible = todayOnly ? reservations.filter((r) => r.date === todayISO()) : reservations;
  const pendingCount = reservations.filter((r) => r.status !== "confirmed").length;
  const confirmedCount = reservations.length - pendingCount;

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-burgundy/10 bg-cream/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Logo La Table de Saint-François" className="h-10 w-10 rounded-full ring-1 ring-burgundy/10" />
            <div>
              <p className="font-serif text-lg font-bold leading-none text-burgundy">Espace Employés</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">La Table de Saint-François</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-burgundy hover:text-burgundy"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-3.5 w-3.5" />
            Déconnexion
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:px-16">
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {[
            { label: "Réservations", value: reservations.length, testid: "admin-stat-total" },
            { label: "En attente", value: pendingCount, testid: "admin-stat-pending" },
            { label: "Confirmées", value: confirmedCount, testid: "admin-stat-confirmed" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-stone-200 bg-white p-4 sm:p-6" data-testid={s.testid}>
              <p className="font-serif text-2xl font-bold text-burgundy sm:text-4xl">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-2xl font-semibold text-stone-900">Réservations</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setTodayOnly(false)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${!todayOnly ? "bg-burgundy text-cream" : "border border-stone-300 text-stone-600 hover:border-burgundy hover:text-burgundy"}`}
              data-testid="admin-filter-all"
            >
              Toutes
            </button>
            <button
              onClick={() => setTodayOnly(true)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${todayOnly ? "bg-burgundy text-cream" : "border border-stone-300 text-stone-600 hover:border-burgundy hover:text-burgundy"}`}
              data-testid="admin-filter-today"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Aujourd'hui
            </button>
          </div>
        </div>

        {loading ? (
          <p className="mt-10 text-center text-sm text-stone-500" data-testid="admin-loading">Chargement des réservations…</p>
        ) : visible.length === 0 ? (
          <p className="mt-10 rounded-2xl border border-dashed border-stone-300 bg-white p-10 text-center text-sm text-stone-500" data-testid="admin-empty">
            {todayOnly ? "Aucune réservation pour aujourd'hui." : "Aucune réservation pour le moment."}
          </p>
        ) : (
          <div className="mt-6 space-y-3" data-testid="admin-reservation-list">
            {visible.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between"
                data-testid={`admin-reservation-${r.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-serif text-lg font-semibold text-stone-900">{r.name}</p>
                    {r.status === "confirmed" ? (
                      <span className="flex items-center gap-1.5 rounded-full bg-sage-light px-3 py-1 text-[11px] font-semibold text-sage">
                        <BadgeCheck className="h-3.5 w-3.5" /> Confirmée
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 rounded-full bg-gold-light px-3 py-1 text-[11px] font-semibold text-amber-900">
                        <Hourglass className="h-3.5 w-3.5" /> En attente
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm capitalize text-stone-600">
                    {formatDateFR(r.date)} · {r.service} · {r.guests} personne{r.guests > 1 ? "s" : ""}
                  </p>
                  {r.email && <p className="mt-0.5 text-sm text-stone-500">{r.email}</p>}
                  {r.notes && <p className="mt-1 text-sm italic text-stone-500">« {r.notes} »</p>}
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-400">
                    Reçue le {new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {r.status !== "confirmed" && (
                    <button
                      onClick={() => confirm(r)}
                      className="flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-sage/90"
                      data-testid={`confirm-reservation-${r.id}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                      Confirmer
                    </button>
                  )}
                  <button
                    onClick={() => remove(r)}
                    className="flex items-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
                    data-testid={`delete-reservation-${r.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Supprimer
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        <AdminNotifications headers={headers} />
        <AdminGallery headers={headers} />
      </main>
    </div>
  );
}
